import {
  ArrowRight,
  ArrowUpRight,
  AndroidLogo,
  AppleLogo,
  Bank,
  Brain,
  Briefcase,
  ChartLineUp,
  Check,
  Checks,
  CreditCard,
  DeviceMobile,
  Fingerprint,
  Gauge,
  GlobeHemisphereWest,
  Lightning,
  List,
  LockKey,
  Microphone,
  Receipt,
  Robot,
  ShieldCheck,
  Sparkle,
  Stack,
  TrendUp,
  Waveform,
  X,
  type Icon,
} from "@phosphor-icons/react";
import {
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { VisualEmotion, VisualStateName } from "@billy/shared-types";

import {
  PLAN_FACTS,
  ROUTE_TITLES,
  allowlistedAppStoreUrl,
  leadPayloadFromForm,
  normalizeMarketingRoute,
  type MarketingRoute,
} from "./marketing-content";

const BillyRenderer3D = lazy(async () => {
  const module = await import("./visual/BillyRenderer3D");
  return { default: module.BillyRenderer3D };
});

type RouteLinkProps = {
  to: MarketingRoute;
  hash?: `#${string}`;
  children: ReactNode;
  className?: string;
  onNavigate?: () => void;
  ariaLabel?: string;
};

type VisualProfile = {
  state: VisualStateName;
  emotion: VisualEmotion;
  baseIntensity: number;
};

const VISUAL_PROFILES: Readonly<Record<MarketingRoute, VisualProfile>> = {
  "/": { state: "idle", emotion: "warm", baseIntensity: 0.28 },
  "/billy-bank": { state: "using_tool", emotion: "focused", baseIntensity: 0.52 },
  "/billy-ia": { state: "thinking", emotion: "curious", baseIntensity: 0.58 },
  "/offres": { state: "listening", emotion: "calm", baseIntensity: 0.36 },
};

const NAV_ITEMS: readonly { path: MarketingRoute; label: string }[] = [
  { path: "/", label: "Découvrir" },
  { path: "/billy-bank", label: "Billy Bank" },
  { path: "/billy-ia", label: "Billy IA" },
  { path: "/offres", label: "Offres & contact" },
];

type BillyEdition = "pro" | "infinity";

const EDITION_ORDER: readonly BillyEdition[] = ["pro", "infinity"];

const EDITION_CONTENT = {
  pro: {
    label: "Billy Pro",
    edition: "PRO",
    title: "Le pilotage quotidien, clair et connecté.",
    description: "Le socle opérationnel Billy pour structurer la voix, les automatismes, les connexions et le pilotage de l’entreprise.",
    metrics: [
      [PLAN_FACTS.pro.limits.conversationTurns, "tours de conversation"],
      [PLAN_FACTS.pro.limits.retention, "de mémoire bornée"],
      [PLAN_FACTS.pro.limits.storage, PLAN_FACTS.pro.limits.storageQualifier],
    ],
    benefits: [
      "Billy IA et interface vocale",
      "Marketing, vente et administration",
      "Connexions et API",
    ],
    packs: ["Billy IA", "Voix", "Pilotage", "Connexions & API"],
  },
  infinity: {
    label: "Billy Infinity",
    edition: "INFINITY",
    title: "Plus de contexte. Toujours sous votre contrôle.",
    description: "L’expérience Billy étendue pour les organisations qui veulent plus de contexte, plus de capacité et l’accès Billy Bank avancé selon leurs droits serveur.",
    metrics: [
      [PLAN_FACTS.infinity.limits.conversationTurns, "tours de conversation"],
      [PLAN_FACTS.infinity.limits.retention, "de mémoire bornée"],
      [PLAN_FACTS.infinity.limits.storage, PLAN_FACTS.infinity.limits.storageQualifier],
    ],
    benefits: [
      "Capacités de Billy Pro",
      "Expérience Billy Bank avancée · phase pilote",
      "Accès selon éligibilité et droits serveur",
    ],
    packs: ["Socle Billy Pro", "Mémoire étendue", "Billy Bank avancé", "Droits serveur"],
  },
} as const;

function scrollToHash(hash: string) {
  const rawId = hash.startsWith("#") ? hash.slice(1) : hash;
  let id = rawId;
  try {
    id = decodeURIComponent(rawId);
  } catch {
    // Keep malformed external hashes inert instead of raising asynchronously.
  }
  document.getElementById(id)?.scrollIntoView({ block: "start" });
}

function RouteLink({ to, hash, children, className, onNavigate, ariaLabel }: RouteLinkProps) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) return;
    event.preventDefault();
    const destination = `${to}${hash ?? ""}`;
    if (`${window.location.pathname}${window.location.hash}` !== destination) {
      window.history.pushState({}, "", destination);
    }
    window.dispatchEvent(new PopStateEvent("popstate"));
    if (hash) {
      window.requestAnimationFrame(() => scrollToHash(hash));
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
    onNavigate?.();
  };

  return (
    <a href={`${to}${hash ?? ""}`} onClick={handleClick} className={className} aria-label={ariaLabel}>
      {children}
    </a>
  );
}

function useRoute(): MarketingRoute {
  const [route, setRoute] = useState<MarketingRoute>(() => normalizeMarketingRoute(window.location.pathname));

  useEffect(() => {
    const sync = () => setRoute(normalizeMarketingRoute(window.location.pathname));
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  useEffect(() => {
    document.title = ROUTE_TITLES[route];
    const hash = window.location.hash;
    if (hash) {
      window.requestAnimationFrame(() => scrollToHash(hash));
    }
  }, [route]);

  return route;
}

function useRevealOnScroll(route: MarketingRoute) {
  useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((element) => element.dataset.visible = "true");
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          (entry.target as HTMLElement).dataset.visible = "true";
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: "0px 0px -8%", threshold: 0.08 });
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [route]);
}

function useScrollMotion(route: MarketingRoute) {
  const [motion, setMotion] = useState({
    progress: 0,
    audioLevel: 0,
    intensityLift: 0,
    pathX: 0,
    pathY: 0,
    scrollRotation: 0,
  });

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setMotion({
        progress: 0,
        audioLevel: 0,
        intensityLift: 0,
        pathX: 0,
        pathY: 0,
        scrollRotation: 0,
      });
      return;
    }
    let previousY = window.scrollY;
    let frame = 0;
    let settleTimer = 0;
    const update = () => {
      frame = 0;
      const range = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const progress = Math.min(1, Math.max(0, window.scrollY / range));
      const velocity = Math.min(1, Math.abs(window.scrollY - previousY) / 72);
      const pathAmplitude = window.innerWidth <= 560 ? 3 : 8;
      previousY = window.scrollY;
      setMotion({
        progress,
        audioLevel: velocity * 0.18,
        intensityLift: progress * 0.2,
        pathX: route === "/" ? Math.sin(progress * Math.PI * 2) * pathAmplitude : 0,
        pathY: route === "/" ? (progress - 0.5) * (window.innerWidth <= 560 ? 8 : 16) : 0,
        scrollRotation: route === "/" ? progress * 0.72 : 0,
      });
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        setMotion((current) => ({ ...current, audioLevel: 0 }));
      }, 120);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
    };
  }, [route]);

  return motion;
}

function useSectionPulse(route: MarketingRoute) {
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    if (route !== "/" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPulse(0);
      return;
    }

    const sections = Array.from(document.querySelectorAll<HTMLElement>("[data-billy-section]"));
    let activeSection = "";
    let resetTimer = 0;
    const observer = new IntersectionObserver((entries) => {
      const current = entries
        .filter((entry) => entry.isIntersecting)
        .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];
      if (!current) return;

      const section = (current.target as HTMLElement).dataset.billySection ?? "";
      if (!section || section === activeSection) return;
      activeSection = section;
      window.clearTimeout(resetTimer);
      setPulse(0.42);
      resetTimer = window.setTimeout(() => setPulse(0), 540);
    }, { rootMargin: "-18% 0px -34%", threshold: [0.08, 0.28, 0.5] });

    sections.forEach((section) => observer.observe(section));
    return () => {
      observer.disconnect();
      window.clearTimeout(resetTimer);
    };
  }, [route]);

  return pulse;
}

function Header({ route }: { route: MarketingRoute }) {
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [route]);

  return (
    <header className="site-header">
      <div className="header-inner">
        <RouteLink to="/" className="header-brand" ariaLabel="Billy Bank, accueil">
          <img src="/brand/billy-bank-wordmark.svg" alt="Billy Bank" />
        </RouteLink>

        <nav className="desktop-nav" aria-label="Navigation principale">
          {NAV_ITEMS.map((item) => (
            <RouteLink
              key={item.path}
              to={item.path}
              className={route === item.path ? "nav-link is-active" : "nav-link"}
            >
              {item.label}
            </RouteLink>
          ))}
        </nav>

        <RouteLink to="/offres" className="header-cta">
          Demander une présentation <ArrowUpRight weight="bold" aria-hidden="true" />
        </RouteLink>

        <button
          className="menu-button"
          type="button"
          aria-expanded={open}
          aria-controls="mobile-navigation"
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X weight="bold" /> : <List weight="bold" />}
        </button>
      </div>

      <nav id="mobile-navigation" className={open ? "mobile-nav is-open" : "mobile-nav"} aria-label="Navigation mobile">
        {NAV_ITEMS.map((item, index) => (
          <RouteLink
            key={item.path}
            to={item.path}
            className={route === item.path ? "mobile-nav-link is-active" : "mobile-nav-link"}
            onNavigate={() => setOpen(false)}
          >
            <span>0{index + 1}</span>{item.label}
          </RouteLink>
        ))}
      </nav>
    </header>
  );
}

function BillyBackdrop({ route, edition }: { route: MarketingRoute; edition: BillyEdition }) {
  const baseProfile = VISUAL_PROFILES[route];
  const effectiveEdition: BillyEdition = route === "/" ? edition : "pro";
  const profile = route === "/"
    ? { state: "thinking" as const, emotion: "focused" as const, baseIntensity: 0.46 }
    : baseProfile;
  const motion = useScrollMotion(route);
  const sectionPulse = useSectionPulse(route);
  const [editionPulse, setEditionPulse] = useState(0);

  useEffect(() => {
    if (route !== "/" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setEditionPulse(0);
      return;
    }
    setEditionPulse(0.34);
    const timer = window.setTimeout(() => setEditionPulse(0), 420);
    return () => window.clearTimeout(timer);
  }, [edition, route]);

  const backdropStyle = {
    "--billy-backdrop-x": `${motion.pathX}vw`,
    "--billy-backdrop-y": `${motion.pathY}vh`,
  } as CSSProperties;

  return (
    <div className="billy-backdrop" data-edition={effectiveEdition} style={backdropStyle} aria-hidden="true">
      <Suspense fallback={<span className={`renderer-fallback renderer-fallback--${effectiveEdition}`} />}>
        <BillyRenderer3D
          appearance={effectiveEdition === "infinity" ? "infinity" : "classic"}
          state={profile.state}
          emotion={profile.emotion}
          audioLevel={Math.max(motion.audioLevel, editionPulse, sectionPulse)}
          intensity={Math.min(1, profile.baseIntensity + motion.intensityLift)}
          eventTimestamp={`marketing-${route}-${effectiveEdition}`}
          quality="auto"
          zoom={0.76 + motion.progress * 0.08}
          scrollRotation={motion.scrollRotation}
        />
      </Suspense>
    </div>
  );
}

function ScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const range = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const ratio = Math.min(1, Math.max(0, window.scrollY / range));
      barRef.current?.style.setProperty("--scroll-progress", String(ratio));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);
  return <div ref={barRef} className="scroll-progress" aria-hidden="true" />;
}

function SectionLabel({ index, children }: { index: string; children: ReactNode }) {
  return <p className="section-label"><span>{index}</span>{children}</p>;
}

function StatusPill({ children, tone = "light" }: { children: ReactNode; tone?: "light" | "dark" | "pink" }) {
  return <span className={`status-pill status-pill--${tone}`}>{children}</span>;
}

function PhonePreview({
  src,
  alt,
  label,
  detail = "Aperçu de démonstration",
  className = "",
  eager = false,
}: {
  src: string;
  alt: string;
  label: string;
  detail?: string;
  className?: string;
  eager?: boolean;
}) {
  return (
    <figure className={`phone-preview ${className}`}>
      <div className="phone-preview__frame">
        <span className="phone-preview__screen">
          <img src={src} alt={alt} loading={eager ? "eager" : "lazy"} />
        </span>
      </div>
      <figcaption>{label}<span>{detail}</span></figcaption>
    </figure>
  );
}

function FeatureCard({ icon: IconComponent, number, title, children }: {
  icon: Icon;
  number: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <article className="feature-card" data-reveal>
      <div className="feature-card__top"><span>{number}</span><IconComponent weight="duotone" aria-hidden="true" /></div>
      <h3>{title}</h3>
      <p>{children}</p>
    </article>
  );
}

function PageIntro({ eyebrow, title, children, actions }: {
  eyebrow: string;
  title: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="page-intro section-shell">
      <div className="page-intro__copy" data-reveal>
        <p className="eyebrow"><Sparkle weight="fill" aria-hidden="true" />{eyebrow}</p>
        <h1>{title}</h1>
        <p className="lead">{children}</p>
        {actions && <div className="button-row">{actions}</div>}
      </div>
    </section>
  );
}

function HomePage({
  appStoreUrl,
  edition,
  onEditionChange,
}: {
  appStoreUrl?: string;
  edition: BillyEdition;
  onEditionChange: (edition: BillyEdition) => void;
}) {
  return (
    <>
      <section className="home-hero section-shell" data-billy-section="hero">
        <div className="home-hero__copy" data-reveal>
          <p className="eyebrow"><Sparkle weight="fill" aria-hidden="true" />Core Pilot Brain · pilote privé</p>
          <h1>L’intelligence qui <em>pilote</em> votre entreprise.</h1>
          <p className="lead">
            Billy réunit vos décisions, vos finances et vos opérations dans une interface vocale pensée pour comprendre, proposer et agir sous votre contrôle.
          </p>
          <div className="button-row">
            <RouteLink to="/offres" className="button button--primary">
              Découvrir le pilote <ArrowRight weight="bold" aria-hidden="true" />
            </RouteLink>
            <RouteLink to="/billy-bank" className="button button--ghost">
              Explorer Billy Bank
            </RouteLink>
          </div>
          <div className="availability-row" aria-label="Disponibilité">
            <StatusPill tone="dark"><AppleLogo weight="fill" />Pilote iPhone disponible</StatusPill>
            <StatusPill><AndroidLogo weight="fill" />Android · prochainement</StatusPill>
          </div>
        </div>

        <div className="home-hero__visual" data-reveal>
          <PhonePreview
            src={edition === "pro" ? "/screens/home-pro.png" : "/screens/home-infinity.png"}
            alt={`Interface native actuelle de l’accueil ${edition === "pro" ? "Billy Pro" : "Billy Infinity"} sur iPhone`}
            label={edition === "pro" ? "Accueil Billy Pro" : "Accueil Billy Infinity"}
            detail="Interface native actuelle"
            className="phone-preview--hero"
            eager
          />
          <div className="floating-note floating-note--top"><Waveform weight="bold" />Réflexion en cours</div>
          <div className="floating-note floating-note--bottom"><Checks weight="bold" />Action sous contrôle</div>
        </div>
      </section>

      <section className="signal-strip" aria-label="Piliers Billy" data-billy-section="signals">
        <span>Billy IA</span><i />
        <span>Pilotage financier</span><i />
        <span>Voix</span><i />
        <span>Automatisations</span><i />
        <span>Connexions & API</span>
      </section>

      <section className="story-section section-shell" data-billy-section="intelligence">
        <div className="story-heading" data-reveal>
          <SectionLabel index="01">Une seule intelligence</SectionLabel>
          <h2>Vous gardez la vision.<br />Billy orchestre le reste.</h2>
          <p>Une expérience claire, conçue pour passer de l’information à la décision sans multiplier les tableaux de bord.</p>
        </div>
        <div className="feature-grid">
          <FeatureCard icon={Microphone} number="01" title="Parlez naturellement">
            Lancez une demande à la voix. Billy structure la conversation sans imposer un champ de saisie permanent.
          </FeatureCard>
          <FeatureCard icon={Brain} number="02" title="Comprenez avant d’agir">
            Les sources, les impacts et le niveau de risque accompagnent chaque proposition.
          </FeatureCard>
          <FeatureCard icon={ShieldCheck} number="03" title="Validez ce qui compte">
            Les actions sensibles passent par une approbation explicite avant toute exécution.
          </FeatureCard>
        </div>
      </section>

      <EditionShowcase appStoreUrl={appStoreUrl} edition={edition} onEditionChange={onEditionChange} />

      <section className="split-showcase section-shell" data-billy-section="finance">
        <div className="split-showcase__media" data-reveal>
          <PhonePreview src="/screens/bank.jpg" alt="Aperçu de démonstration du tableau de bord Billy Bank" label="Billy Bank" />
          <PhonePreview src="/screens/notifications.jpg" alt="Aperçu de démonstration des notifications Billy" label="Vos priorités" className="phone-preview--rear" />
        </div>
        <div className="split-showcase__copy" data-reveal>
          <SectionLabel index="02">Finance & entreprise</SectionLabel>
          <h2>Du signal à la prochaine action.</h2>
          <p className="lead-small">Billy met en perspective vos échéances, vos budgets et vos opérations — avec leur provenance et leurs états réels.</p>
          <ul className="check-list">
            <li><Check weight="bold" />Vue financière unifiée et explicable</li>
            <li><Check weight="bold" />Plan d’action, approbation, exécution, résultat</li>
            <li><Check weight="bold" />Notifications, profil et préférences</li>
            <li><Check weight="bold" />Connecteurs pilotés côté serveur</li>
          </ul>
          <RouteLink to="/billy-ia" className="text-link">Voir comment Billy réfléchit <ArrowRight weight="bold" /></RouteLink>
        </div>
      </section>

      <Callout />
    </>
  );
}

function EditionShowcase({
  appStoreUrl,
  edition,
  onEditionChange,
}: {
  appStoreUrl?: string;
  edition: BillyEdition;
  onEditionChange: (edition: BillyEdition) => void;
}) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const officialAppStoreUrl = allowlistedAppStoreUrl(appStoreUrl);

  const selectTab = (index: number, focus = false) => {
    const nextIndex = (index + EDITION_ORDER.length) % EDITION_ORDER.length;
    onEditionChange(EDITION_ORDER[nextIndex]);
    if (focus) tabRefs.current[nextIndex]?.focus();
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      selectTab(index + 1, true);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      selectTab(index - 1, true);
    } else if (event.key === "Home") {
      event.preventDefault();
      selectTab(0, true);
    } else if (event.key === "End") {
      event.preventDefault();
      selectTab(EDITION_ORDER.length - 1, true);
    }
  };

  return (
    <section id="billy-editions" className="edition-showcase section-shell" data-edition={edition} data-billy-section="editions">
      <div className="edition-showcase__tabs" role="tablist" aria-label="Choisir une édition Billy" aria-orientation="horizontal">
        {EDITION_ORDER.map((key, index) => (
          <button
            key={key}
            ref={(element) => { tabRefs.current[index] = element; }}
            id={`edition-tab-${key}`}
            type="button"
            role="tab"
            aria-selected={edition === key}
            aria-controls={`edition-panel-${key}`}
            tabIndex={edition === key ? 0 : -1}
            onClick={() => selectTab(index)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
          >
            <span className="edition-showcase__tab-preview" aria-hidden="true">
              <img
                src={key === "pro" ? "/media/billy-pro.png" : "/media/billy-infinity.png"}
                alt=""
              />
            </span>
            <span className="edition-showcase__tab-label">{EDITION_CONTENT[key].label}</span>
          </button>
        ))}
      </div>

      <div className="edition-showcase__copy" data-reveal>
        {EDITION_ORDER.map((key) => {
          const panel = EDITION_CONTENT[key];
          return (
            <div
              key={key}
              id={`edition-panel-${key}`}
              role="tabpanel"
              aria-labelledby={`edition-tab-${key}`}
              tabIndex={0}
              hidden={edition !== key}
              className="edition-showcase__panel"
            >
              <p className="edition-showcase__edition"><span>Billy</span>{panel.edition}</p>
              <h2>{panel.title}</h2>
              <p className="edition-showcase__description">{panel.description}</p>
              <div className="edition-showcase__metrics" aria-label={`Métriques ${panel.label}`}>
                {panel.metrics.map(([value, label]) => (
                  <span key={label}><strong>{value}</strong><small>{label}</small></span>
                ))}
              </div>
              <ul className="edition-showcase__benefits">
                {panel.benefits.map((benefit) => <li key={benefit}><Check weight="bold" />{benefit}</li>)}
              </ul>
              <div className="edition-showcase__packs" aria-label={`Capacités ${panel.label}`}>
                <strong>Capacités incluses</strong>
                <div>{panel.packs.map((pack) => <span key={pack}>{pack}</span>)}</div>
              </div>
              <RouteLink to="/offres" className={edition === "pro" ? "button button--dark" : "button button--light"}>
                Voir le pack {panel.label} <ArrowRight weight="bold" aria-hidden="true" />
              </RouteLink>
            </div>
          );
        })}
      </div>

      <div className="edition-showcase__availability" data-reveal>
        {officialAppStoreUrl ? (
          <a href={officialAppStoreUrl} target="_blank" rel="noreferrer"><AppleLogo weight="fill" />Voir sur l’App Store</a>
        ) : (
          <span>
            <AppleLogo weight="fill" />
            <span><strong>Pilote iPhone</strong><small>Publication App Store à venir</small></span>
            <RouteLink to="/offres" hash="#contact">Demander un accès iPhone</RouteLink>
          </span>
        )}
        <span>
          <AndroidLogo weight="fill" />
          <span><strong>Android</strong><small>Prochainement</small></span>
        </span>
      </div>
    </section>
  );
}

function BillyBankPage() {
  return (
    <>
      <PageIntro
        eyebrow="Pilotage financier augmenté"
        title={<>La finance devient <em>lisible</em>, actionnable, humaine.</>}
        actions={
          <>
            <RouteLink to="/offres" className="button button--primary">Demander une présentation <ArrowRight weight="bold" /></RouteLink>
            <a href="#experience" className="button button--ghost">Voir l’expérience</a>
          </>
        }
      >
        Billy Bank est l’interface de pilotage financier de Billy : une expérience néobanque en phase pilote, reliée à vos données plutôt qu’à des chiffres inventés.
      </PageIntro>

      <section id="experience" className="bank-stage section-shell">
        <div className="bank-stage__brand" data-reveal>
          <img src="/brand/billy-bank-wordmark.svg" alt="Billy Bank" />
          <div className="mini-metrics">
            <div><Receipt weight="duotone" /><strong>Échéances</strong><span>Sources visibles</span></div>
            <div><TrendUp weight="duotone" /><strong>Stratégie</strong><span>Conseils explicables</span></div>
            <div><Gauge weight="duotone" /><strong>Trésorerie</strong><span>Données rapprochées</span></div>
          </div>
        </div>
        <div className="bank-stage__phones" data-reveal>
          <PhonePreview src="/screens/bank.jpg" alt="Aperçu de démonstration du hub Billy Bank" label="Vue Billy Bank" className="phone-preview--bank-main" />
          <PhonePreview src="/screens/trader.jpg" alt="Aperçu de démonstration du Robot Trader en simulation" label="Robot Trader · simulation" className="phone-preview--bank-side" />
        </div>
      </section>

      <section className="cards-section section-shell">
        <div className="story-heading" data-reveal>
          <SectionLabel index="01">Cartes Billy Infinity</SectionLabel>
          <h2>Athena pour l’entreprise.<br />Hannibal pour l’international.</h2>
          <p>Deux concepts de carte intégrés au parcours pilote. L’émission, les limites et la disponibilité restent soumises à l’éligibilité et à l’approbation d’un partenaire réglementé.</p>
        </div>
        <div className="bank-card-grid">
          <article className="concept-card concept-card--athena" data-reveal>
            <span className="concept-card__shine" />
            <div className="concept-card__head"><img src="/brand/billy-app-icon.svg" alt="" /><span>CONCEPT PILOTE</span></div>
            <div className="concept-card__name">Athena</div>
            <div className="concept-card__footer"><span>BUSINESS</span><CreditCard weight="thin" /></div>
          </article>
          <article className="concept-card concept-card--hannibal" data-reveal>
            <span className="concept-card__shine" />
            <div className="concept-card__head"><img src="/brand/billy-app-icon.svg" alt="" /><span>CONCEPT PILOTE</span></div>
            <div className="concept-card__name">Hannibal</div>
            <div className="concept-card__footer"><span>INTERNATIONAL</span><GlobeHemisphereWest weight="thin" /></div>
          </article>
        </div>
      </section>

      <section className="finance-capabilities section-shell">
        <SectionLabel index="02">Un cockpit, pas un grand livre parallèle</SectionLabel>
        <div className="capability-list">
          {[
            [Bank, "Vision consolidée", "Soldes, budgets, dépenses et transactions gardent leur source et leur horodatage."],
            [Receipt, "Charges à prévoir", "Échéances Odoo, engagements Billy et disponibilité bancaire restent séparés jusqu’au rapprochement."],
            [ChartLineUp, "Stratégies financières", "Croissance, stabilité, relance ou désendettement : les recommandations restent non exécutoires."],
            [Robot, "Robot Trader", "Veille et scénarios uniquement. L’exécution réelle est explicitement désactivée."],
          ].map(([IconComponent, title, text], index) => {
            const CapabilityIcon = IconComponent as Icon;
            return (
              <article key={String(title)} className="capability-row" data-reveal>
                <span className="capability-row__index">0{index + 1}</span>
                <CapabilityIcon weight="duotone" aria-hidden="true" />
                <h3>{String(title)}</h3>
                <p>{String(text)}</p>
              </article>
            );
          })}
        </div>
      </section>

      <aside className="compliance-note section-shell" data-reveal>
        <LockKey weight="duotone" aria-hidden="true" />
        <div><strong>Un périmètre volontairement clair.</strong><p>Billy Bank est une interface de pilotage financier et une expérience néobanque en phase pilote. Billy n’est pas présenté comme une banque réglementée. Les services de compte, carte ou paiement dépendront de partenaires autorisés et de l’éligibilité.</p></div>
      </aside>

      <Callout />
    </>
  );
}

function BillyIAPage() {
  const steps = [
    { number: "01", label: "Comprendre", title: "Billy écoute le contexte", text: "La demande est replacée dans l’entreprise, ses priorités et les droits du compte.", icon: Microphone },
    { number: "02", label: "Expliquer", title: "Billy prépare un plan", text: "Objectif, sources, durée, impacts et risque sont présentés avant l’action.", icon: Brain },
    { number: "03", label: "Contrôler", title: "Vous gardez la décision", text: "Les opérations sensibles passent par une validation forte, bornée et traçable.", icon: Fingerprint },
    { number: "04", label: "Exécuter", title: "L’activité reste visible", text: "Étapes, événements, agents, résultat et prochaine action restent lisibles du début à la fin.", icon: Lightning },
  ] as const;

  return (
    <>
      <PageIntro
        eyebrow="Billy Core Pilot Brain"
        title={<>Une IA qui rend son <em>raisonnement visible.</em></>}
        actions={
          <>
            <RouteLink to="/offres" className="button button--primary">Parler de votre usage <ArrowRight weight="bold" /></RouteLink>
            <a href="#parcours" className="button button--ghost">Suivre le parcours</a>
          </>
        }
      >
        Billy ne se limite pas à répondre. Il structure un plan, demande les validations nécessaires et restitue ce qui a réellement été fait.
      </PageIntro>

      <section id="parcours" className="ai-steps section-shell">
        <SectionLabel index="01">De l’intention au résultat</SectionLabel>
        <div className="ai-steps__grid">
          {steps.map((step) => {
            const StepIcon = step.icon;
            return (
              <article className="ai-step" key={step.number} data-reveal>
                <div className="ai-step__meta"><span>{step.number}</span><em>{step.label}</em></div>
                <StepIcon weight="duotone" aria-hidden="true" />
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="action-sequence section-shell">
        <div className="story-heading" data-reveal>
          <SectionLabel index="02">Le contrôle en images</SectionLabel>
          <h2>Chaque étape a un état.<br />Chaque état a une preuve.</h2>
        </div>
        <div className="sequence-rail" aria-label="Aperçus de démonstration du parcours d’action">
          <PhonePreview src="/screens/plan.jpg" alt="Aperçu de démonstration du plan d’action Billy" label="Plan explicable" />
          <PhonePreview src="/screens/approval.jpg" alt="Aperçu de démonstration de la validation forte Billy" label="Validation" />
          <PhonePreview src="/screens/execution.jpg" alt="Aperçu de démonstration de l’exécution Billy" label="Exécution" />
          <PhonePreview src="/screens/result.jpg" alt="Aperçu de démonstration du résultat Billy" label="Résultat" />
        </div>
      </section>

      <section className="ai-principles section-shell">
        <div className="ai-principles__visual" data-reveal>
          <img src="/brand/billy-wordmark.svg" alt="Billy" />
          <div className="thought-signal"><span /><span /><span /><span /><span /></div>
          <p>Écoute · réflexion · proposition · approbation · action</p>
        </div>
        <div className="ai-principles__copy" data-reveal>
          <SectionLabel index="03">Une IA avec des limites explicites</SectionLabel>
          <h2>Rapide quand il le faut. Détaillée quand c’est utile.</h2>
          <div className="principle-list">
            <p><ShieldCheck weight="duotone" /><span><strong>Actions gouvernées</strong>Les niveaux d’approbation encadrent les opérations sensibles.</span></p>
            <p><Stack weight="duotone" /><span><strong>Mémoire bornée</strong>Les limites dépendent de l’offre et restent configurées côté serveur.</span></p>
            <p><LockKey weight="duotone" /><span><strong>Données maîtrisées</strong>Les secrets ne résident pas dans l’interface cliente.</span></p>
          </div>
        </div>
      </section>

      <Callout />
    </>
  );
}

function OffersPage({ route }: { route: MarketingRoute }) {
  return (
    <>
      <PageIntro
        eyebrow="Deux expériences, une même exigence"
        title={<>Choisissez le niveau de <em>pilotage</em> qui vous ressemble.</>}
      >
        Billy Pro pose le socle opérationnel. Billy Infinity étend la mémoire, les capacités et l’expérience néobanque — selon les droits réellement accordés au compte.
      </PageIntro>

      <section className="plans section-shell">
        <article className="plan-card plan-card--pro" data-reveal>
          <div className="plan-card__visual"><img src="/media/billy-pro.png" alt="Rendu officiel Billy Pro rose" /></div>
          <div className="plan-card__copy">
            <span className="plan-card__edition">PRO</span>
            <h2>{PLAN_FACTS.pro.name}</h2>
            <p>Pour structurer le pilotage quotidien, la voix, les automatismes et les connexions de l’entreprise.</p>
            <ul>
              <li><Check weight="bold" />Billy IA et interface vocale</li>
              <li><Check weight="bold" />Marketing, vente & administration</li>
              <li><Check weight="bold" />{PLAN_FACTS.pro.memory}</li>
              <li><Check weight="bold" />{PLAN_FACTS.pro.retention}</li>
              <li><Check weight="bold" />Connexions & API</li>
            </ul>
            <a href="#contact" className="button button--dark">Demander Billy Pro <ArrowRight weight="bold" /></a>
          </div>
        </article>

        <article className="plan-card plan-card--infinity" data-reveal>
          <div className="plan-card__visual"><img src="/media/billy-infinity.png" alt="Rendu officiel Billy Infinity noir chromé" /></div>
          <div className="plan-card__copy">
            <span className="plan-card__edition">INFINITY</span>
            <h2>{PLAN_FACTS.infinity.name}</h2>
            <p>Pour les organisations qui veulent plus de contexte, plus de capacité et l’expérience Billy Bank avancée.</p>
            <ul>
              <li><Check weight="bold" />Socle Billy Pro</li>
              <li><Check weight="bold" />{PLAN_FACTS.infinity.memory}</li>
              <li><Check weight="bold" />{PLAN_FACTS.infinity.retention}</li>
              <li><Check weight="bold" />Billy Bank avancé · phase pilote</li>
              <li><Check weight="bold" />Accès conditionné aux droits serveur</li>
            </ul>
            <a href="#contact" className="button button--light">Demander Billy Infinity <ArrowRight weight="bold" /></a>
          </div>
        </article>
      </section>

      <section className="availability-section section-shell" data-reveal>
        <div>
          <img src="/brand/billy-app-icon.svg" alt="Icône de l’application Billy" />
          <span><strong>Billy sur votre téléphone</strong>Une expérience mobile-first pensée pour rester proche de vos décisions.</span>
        </div>
        <div className="availability-actions">
          <span className="availability-card"><AppleLogo weight="duotone" /><strong>iPhone</strong><small>Pilote privé disponible</small></span>
          <span className="availability-card is-muted"><AndroidLogo weight="duotone" /><strong>Android</strong><small>Prochainement</small></span>
        </div>
      </section>

      <ContactSection route={route} />
    </>
  );
}

type SubmissionState = "idle" | "submitting" | "success" | "error";

function ContactSection({ route }: { route: MarketingRoute }) {
  const [state, setState] = useState<SubmissionState>("idle");
  const formRef = useRef<HTMLFormElement>(null);
  const idempotencyKey = useRef(globalThis.crypto.randomUUID());
  const submittedFingerprint = useRef("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    setState("submitting");
    try {
      const payload = leadPayloadFromForm(new FormData(form), route, window.location.search);
      const fingerprint = JSON.stringify(payload);
      if (submittedFingerprint.current && submittedFingerprint.current !== fingerprint) {
        idempotencyKey.current = globalThis.crypto.randomUUID();
      }
      submittedFingerprint.current = fingerprint;
      const response = await fetch("/api/marketing/leads", {
        method: "POST",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Idempotency-Key": idempotencyKey.current,
        },
        body: JSON.stringify(payload),
      });
      if (response.status !== 201 && response.status !== 202) {
        throw new Error(`lead_request_${response.status}`);
      }
      setState("success");
      form.reset();
      submittedFingerprint.current = "";
      idempotencyKey.current = globalThis.crypto.randomUUID();
    } catch {
      setState("error");
    }
  };

  return (
    <section id="contact" className="contact-section section-shell">
      <div className="contact-copy" data-reveal>
        <SectionLabel index="Contact">Construisons le bon pilote</SectionLabel>
        <h2>Parlez-nous de votre entreprise.</h2>
        <p>Décrivez votre besoin. La demande est transmise au service Billy uniquement après confirmation du serveur — jamais simulée dans le navigateur.</p>
        <div className="contact-points">
          <span><Briefcase weight="duotone" />Une présentation adaptée à votre activité</span>
          <span><ShieldCheck weight="duotone" />Aucune activation financière automatique</span>
          <span><Checks weight="duotone" />Votre demande reste traçable</span>
        </div>
      </div>

      <form ref={formRef} className="contact-form" onSubmit={submit} data-reveal>
        <div className="form-row">
          <label>Nom et prénom<input name="name" autoComplete="name" required minLength={2} maxLength={120} /></label>
          <label>Entreprise<input name="company" autoComplete="organization" required minLength={2} maxLength={160} /></label>
        </div>
        <div className="form-row">
          <label>E-mail professionnel<input name="work_email" type="email" autoComplete="email" required maxLength={254} /></label>
          <label>Téléphone <span>(facultatif)</span><input name="phone" type="tel" autoComplete="tel" maxLength={40} /></label>
        </div>
        <label>Votre priorité
          <select name="project" required defaultValue="">
            <option value="" disabled>Choisir un sujet</option>
            <option value="billy-pro">Billy Pro</option>
            <option value="billy-infinity">Billy Infinity</option>
            <option value="billy-bank">Billy Bank</option>
            <option value="integrations">Connexions & automatisations</option>
            <option value="other">Autre besoin</option>
          </select>
        </label>
        <label>Votre projet<textarea name="message" required minLength={20} maxLength={2000} rows={5} placeholder="Contexte, priorités, outils actuels…" /></label>
        <label className="honeypot" aria-hidden="true">Site internet<input name="website" tabIndex={-1} autoComplete="off" /></label>
        <label className="consent-field"><input name="consent" type="checkbox" required /><span>J’accepte que Billy utilise ces informations pour répondre à ma demande, conformément à la <a href="https://billybank.fr/privacy-policy/" target="_blank" rel="noreferrer">politique de confidentialité</a>. Aucun abonnement ni service financier n’est activé par ce formulaire.</span></label>
        <button className="button button--primary form-submit" type="submit" disabled={state === "submitting"}>
          {state === "submitting" ? "Transmission…" : "Envoyer ma demande"}<ArrowRight weight="bold" />
        </button>
        <p className={`form-status form-status--${state}`} role="status" aria-live="polite">
          {state === "success" && "Votre demande a bien été confirmée par le serveur Billy."}
          {state === "error" && "La transmission n’a pas été confirmée. Réessayez dans un instant — aucune fausse demande n’a été enregistrée."}
        </p>
      </form>
    </section>
  );
}

function Callout() {
  return (
    <section className="callout section-shell" data-reveal data-billy-section="callout">
      <div>
        <p className="eyebrow eyebrow--light"><Sparkle weight="fill" />Pilote Billy</p>
        <h2>Votre entreprise mérite une intelligence qui comprend l’ensemble.</h2>
      </div>
      <RouteLink to="/offres" className="button button--light">Découvrir les offres <ArrowRight weight="bold" /></RouteLink>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-main section-shell">
        <div className="footer-brand">
          <img
            src="/brand/billy-bank-footer-lockup.svg"
            alt="Billy Bank — Group Financial Operating System"
            width="1210"
            height="345"
          />
        </div>
        <div className="footer-nav">
          <strong>Produit</strong>
          <RouteLink to="/billy-bank">Billy Bank</RouteLink>
          <RouteLink to="/billy-ia">Billy IA</RouteLink>
          <RouteLink to="/offres">Offres</RouteLink>
        </div>
        <div className="footer-nav">
          <strong>Disponibilité</strong>
          <span>iPhone · pilote privé</span>
          <span>Android · prochainement</span>
          <span>Accès sur invitation</span>
        </div>
        <nav className="footer-nav" aria-label="Informations légales">
          <strong>Informations légales</strong>
          <a href="https://billybank.fr/privacy-policy/">Politique de confidentialité</a>
          <a href="https://billybank.fr/terms-of-service">Conditions d’utilisation</a>
          <a href="https://billybank.fr/support">Support</a>
        </nav>
      </div>
      <div className="footer-legal section-shell">
        <span>© {new Date().getFullYear()} Billy Bank</span>
        <p>Billy Bank est une interface de pilotage financier en phase pilote. Les services réglementés restent soumis à l’éligibilité et aux partenaires autorisés.</p>
      </div>
    </footer>
  );
}

export function MarketingSite({ appStoreUrl }: { appStoreUrl?: string } = {}) {
  const route = useRoute();
  const [homeEdition, setHomeEdition] = useState<BillyEdition>("pro");
  useRevealOnScroll(route);

  const page = useMemo(() => {
    switch (route) {
      case "/billy-bank": return <BillyBankPage />;
      case "/billy-ia": return <BillyIAPage />;
      case "/offres": return <OffersPage route={route} />;
      default: return (
        <HomePage
          appStoreUrl={appStoreUrl}
          edition={homeEdition}
          onEditionChange={setHomeEdition}
        />
      );
    }
  }, [appStoreUrl, homeEdition, route]);

  return (
    <div
      className={`marketing-site route-${route === "/" ? "home" : route.slice(1)}`}
      data-home-edition={route === "/" ? homeEdition : undefined}
    >
      <a className="skip-link" href="#main-content">Aller au contenu</a>
      <ScrollProgress />
      <BillyBackdrop route={route} edition={homeEdition} />
      <Header route={route} />
      <main id="main-content">{page}</main>
      <Footer />
    </div>
  );
}
